<template>
	<div class="project-task-list">
		<div class="project-task-list__header">
			<h3 class="project-task-list__title">
				<a
					:href="`/${Module.TASKS.name}?bookmark_reset=1&bookmark_projects[${projectId}]=${projectId}&bookmark_users[all]=all`"
				>
					{{ $t('project.tab.taskslist') }}
				</a>
			</h3>

			<ProjectTaskListActions
				v-if="!loadingUseTaskStatuses"
				v-model:selected-done-filter="selectedDoneFilter"
				:project="project"
				:use-task-statuses="useTaskStatuses"
				:use-subtasks="isSubtasksEnabled"
				@add-task="
					rootEmit('show-event-popup', {
						eventType: 'task',
						project_id: projectId,
						company_id: project?.company?.id,
					})
				"
				@open-phases-modal="handleOpenPhasesModal"
			/>
		</div>

		<div v-app-loader-top="loading && !isInitialLoad">
			<TaskList
				v-if="!loadingUseTaskStatuses"
				:tasks-sorted="tasksSorted"
				:tasks-unsorted="unsortedTasks"
				:subtasks-unsorted="unsortedSubtasks"
				:tasks-enrichment-data="tasksEnrichmentData"
				:newly-added-task-ids="newlyAddedTaskIds"
				:project="project"
				:phases="project?.projectPhases || []"
				:loading="showInitialSkeletonLoading"
				:loading-more="isLoadingMore"
				:loading-rows-grouped="loadingRows"
				:group-by="TaskListGroupBy.Phases"
				:sort="sortState"
				:task-done-filter="delayedDoneFilter"
				:loading-subtasks="loadingSubtasksRows"
				add-task-enabled
				:show-subtasks="isSubtasksEnabled"
				:use-task-statuses="useTaskStatuses"
				:storage-key="STORAGE_KEYS.PROJECT_TASK_LIST"
				:available-columns="projectTaskListAvailableColumns"
				@update:sort="updateSortState"
				@add-task="onAddTask"
			>
				<template #after-last-task>
					<AppScrollLoader
						:is-loading="loading"
						:is-end-reached="isEndReached"
						:root-margin="SCROLL_LOADER_MARGIN"
						@load-next-batch="onLoadNextBatch"
					/>
				</template>
			</TaskList>
		</div>

		<Teleport to="body">
			<ProjectPhasesModal
				v-if="showPhasesModal"
				:project-id="projectId"
				@close="handlePhasesModalClose"
				@saved="handlePhasesModalSaved"
			/>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Module } from '@/constants/modules';
import {
	type SortState,
	TaskList,
	TaskListGroupBy,
	type TaskListLoadingRows,
	type TaskListLoadingSubtaskRows,
	STORAGE_KEYS,
	useTaskListAvailableColumns,
} from '@/features/Task';
import { useSubtasksPermissions } from '@/composables/permissions';
import {
	ModuleType,
	type PagerInput,
	type Project,
	type ProjectPhase,
	type ProjectTaskListQueryVariables,
	type RetainerPeriod,
	SortDirection,
	type Task,
	type TaskListEnrichmentFieldsFragment,
	type TaskListTaskFragment,
	type TaskOrderingInput,
	type TasksFiltersInput,
	TYPES,
	useProjectTaskListEnrichmentsQuery,
	useProjectTaskListQuery,
	useProjectTaskListTaskSaveMutation,
} from '@/generated/graphql';
import ProjectTaskListActions from './ProjectTaskListActions.vue';
import { ProjectPhasesModal } from '@/features/Project/components/ProjectPhasesModal';
import {
	useBooleanSetting,
	useMfeUtils,
	usePermissions,
	useSocket,
	useTranslation,
} from '@/composables';
import {
	DEFAULT_SORT_STATE,
	COLUMN_HEADER_SORT_MAP,
} from '@/features/Task/components/TaskList/constants';
import { TASK_DONE_FILTER } from '@/features/Task/constants';
import { useLocalStorage } from '@vueuse/core';
import { LocalStorageKeys } from '@/constants/storage';
import { SocketMessageType } from '@/constants/socketMessages';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import { useApolloClient } from '@vue/apollo-composable';
import {
	SuiDataTableSortDirection,
	SuiMessageTypeType,
} from '@developers/scoro-ui';
import { type ShowEventPopupPayload } from '@/composables/useMfeUtils';
import { type ValueOf } from '@/types';
import { AppScrollLoader } from '@/features/App';
import { PermissionAction } from '@/constants/permissions';
import { Constants } from '@/constants';
import { useMessage } from '@/features/Message';
import { Setting } from '@/constants/settings';

const LOAD_BATCH_SIZE = 50;
const SCROLL_LOADER_MARGIN = '800px';

export type ProjectTaskListProps = {
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
};

const props = defineProps<ProjectTaskListProps>();

defineEmits<{
	(e: 'show-event-popup', payload: ShowEventPopupPayload): void;
}>();

const { rootEmit } = useMfeUtils();
const selectedDoneFilter = useLocalStorage<ValueOf<typeof TASK_DONE_FILTER>>(
	LocalStorageKeys.ProjectTaskListDoneFilter,
	TASK_DONE_FILTER.NotDone
);
const { mutate: taskSave } = useProjectTaskListTaskSaveMutation({});
const [useTaskStatuses, { loading: loadingUseTaskStatuses }] =
	useBooleanSetting(Setting.USE_TASK_STATUSES);

const isInitialLoad = ref(true);
const isEndReached = ref(false);
const releaseUnsortedTasksOnNextResult = ref(false);
const unsortedTasks = ref<TaskListTaskFragment[]>([]);
const unsortedSubtasks = ref<Record<Task['id'], TaskListTaskFragment[]>>({});
const newlyAddedTaskIds = ref<Set<string>>(new Set());
const isEditMode = ref(false);
const loadingRows = ref<TaskListLoadingRows>({
	0: [],
});
const loadingSubtasksRows = ref<TaskListLoadingSubtaskRows>({});
const delayedDoneFilter = ref(selectedDoneFilter.value);
const showPhasesModal = ref(false);

const { can } = usePermissions();
const { addMessage } = useMessage();
const { t } = useTranslation();
const { isSubtasksEnabled } = useSubtasksPermissions();
const { projectTaskListAvailableColumns } = useTaskListAvailableColumns();
const { client } = useApolloClient();

const isDoneFilter = computed(() => {
	switch (selectedDoneFilter.value) {
		case TASK_DONE_FILTER.Done:
			return true;
		case TASK_DONE_FILTER.NotDone:
			return false;
		default:
			return undefined;
	}
});

const sortState = useLocalStorage<SortState>(
	LocalStorageKeys.ProjectTaskListSortState,
	DEFAULT_SORT_STATE
);

const taskListParams: ProjectTaskListQueryVariables['taskListParams'] & {
	filters: TasksFiltersInput;
	ordering: TaskOrderingInput;
	pager: PagerInput;
} = {
	filters: {
		projectId: props.projectId,
		isDone: isDoneFilter.value,
		retainerPeriodId: props.retainerPeriodId,
		includeSubtasks: false,
	},
	pager: {
		startFrom: 0,
		limit: LOAD_BATCH_SIZE,
	},
	ordering: {
		orderBy: COLUMN_HEADER_SORT_MAP[sortState.value.columnId],
		direction:
			sortState.value.direction === SuiDataTableSortDirection.ASC
				? SortDirection.Asc
				: SortDirection.Desc,
	},
	module: ModuleType.Projects,
};

const projectParams: ProjectTaskListQueryVariables['projectParams'] = {
	projectId: Number(props.projectId),
	retainerPeriodId: props.retainerPeriodId,
};

const { result, loading, refetch, onResult, fetchMore } =
	useRefetchOnSocketMessage(
		useProjectTaskListQuery({
			taskListParams,
			projectParams,
		}),
		{
			messages: [
				SocketMessageType.TASK_CREATED,
				SocketMessageType.NEW_PHASE_ADDED,
				SocketMessageType.PHASE_CHANGED,
				SocketMessageType.PHASE_DELETED,
			],
			filter: (result) => {
				if (result.type === SocketMessageType.TASK_CREATED) {
					const taskData = result.data;
					return (
						String(taskData.project_id) === props.projectId &&
						!isEditMode.value &&
						!unsortedTasks.value.some(
							(task) =>
								'event_id' in taskData &&
								task.id === String(taskData.event_id)
						) &&
						!Object.values(unsortedSubtasks.value).some(
							(subtasks) =>
								subtasks.some(
									(task) =>
										'event_id' in taskData &&
										task.id === String(taskData.event_id)
								)
						)
					);
				}

				return String(result.data.project_id) === props.projectId;
			},
		}
	);

const { result: taskListEnrichments } = useProjectTaskListEnrichmentsQuery(
	() => ({
		taskListParams,
	})
);

const onLoadNextBatch = async () => {
	if (taskListParams.pager && !isEndReached.value) {
		taskListParams.pager.startFrom += LOAD_BATCH_SIZE;

		await fetchMore({
			variables: {
				taskListParams,
			},
			updateQuery: (prev, { fetchMoreResult }) => {
				if (!fetchMoreResult?.TasksList.length) {
					isEndReached.value = true;
					return prev;
				}

				if (fetchMoreResult?.TasksList.length < LOAD_BATCH_SIZE) {
					isEndReached.value = true;
				}

				return Object.assign({}, prev, {
					TasksList: [
						...prev.TasksList,
						...fetchMoreResult.TasksList,
					],
				});
			},
		});
	}
};

const project = computed(() => result.value?.Project || undefined);

const tasksEnrichmentData = computed<Map<
	string,
	TaskListEnrichmentFieldsFragment
> | null>(() => {
	if (!taskListEnrichments.value?.TasksList) {
		return null;
	}

	const allTasks =
		taskListEnrichments.value.TasksList.flatMap((task) => {
			return [task, ...('subtasks' in task ? task.subtasks || [] : [])];
		}) || [];

	const enrichmentMap = new Map(
		allTasks
			.filter((task) => 'assignees' in task)
			.map((task) => [task.id, task as TaskListEnrichmentFieldsFragment])
	);

	return enrichmentMap;
});

const resetPagerAndRefetch = () => {
	taskListParams.pager.startFrom = 0;
	isEndReached.value = false;

	refetch();
};

watch(isDoneFilter, (val) => {
	taskListParams.filters.isDone = val;

	resetPagerAndRefetch();
});

watch(
	() => props.retainerPeriodId,
	(val) => {
		taskListParams.filters.retainerPeriodId = val;
		projectParams.retainerPeriodId = val;
		releaseUnsortedTasksOnNextResult.value = true;
		resetPagerAndRefetch();
	}
);

const tasksSorted = computed(() => {
	return (
		result.value?.TasksList?.filter(
			(task) =>
				!unsortedTasks.value.find(
					(taskUnsorted) => taskUnsorted.id === task.id
				)
		) || []
	);
});

const onAddTask = async (payload: {
	name: Task['name'];
	projectPhaseId?: ProjectPhase['id'];
	parentId?: Task['id'];
}) => {
	isEditMode.value = true;

	if (!loadingRows.value[payload.projectPhaseId || 0]) {
		loadingRows.value[payload.projectPhaseId || 0] = [];
	}

	if (payload.parentId && !loadingSubtasksRows.value[payload.parentId]) {
		loadingSubtasksRows.value[payload.parentId] = [];
		unsortedSubtasks.value[payload.parentId] = [];
	}

	if (!payload.parentId) {
		loadingRows.value[payload.projectPhaseId || 0].push(payload.name);
	} else {
		loadingSubtasksRows.value[payload.parentId].push(payload.name);
	}

	const result = await taskSave({
		input: {
			name: payload.name,
			projectId: props.projectId,
			projectPhaseId: payload.projectPhaseId,
			parentId: payload.parentId,
		},
	});

	if (!payload.parentId) {
		if (result?.data?.TaskSave) {
			unsortedTasks.value.push(result.data.TaskSave);
			newlyAddedTaskIds.value.add(result.data.TaskSave.id);
		}

		loadingRows.value[payload.projectPhaseId || 0].shift();
	} else {
		if (result?.data?.TaskSave) {
			unsortedSubtasks.value[payload.parentId].push(result.data.TaskSave);
			newlyAddedTaskIds.value.add(result.data.TaskSave.id);
		}

		loadingSubtasksRows.value[payload.parentId].shift();
	}

	isEditMode.value = false;
};

const updateSortState = (newSortState: SortState) => {
	sortState.value.columnId = newSortState.columnId;
	sortState.value.direction = newSortState.direction;

	taskListParams.ordering = {
		orderBy: COLUMN_HEADER_SORT_MAP[newSortState.columnId],
		direction:
			newSortState.direction === SuiDataTableSortDirection.ASC
				? SortDirection.Asc
				: SortDirection.Desc,
	};

	resetPagerAndRefetch();
	releaseUnsortedTasksOnNextResult.value = true;
};

const canModifyProject = computed(
	() => project.value?.permissions?.canModify || false
);

const canCreateTasks = computed(() =>
	can(PermissionAction.CREATE, Constants.Type.Task)
);

const showQuoteToTasksMessage = () => {
	const url = `/${Constants.Module.QUOTES.name}/${Constants.Feature.QUOTE_TO_TASKS}/${project.value?.createTasksFromQuoteId}`;
	addMessage({
		type: SuiMessageTypeType.Info,
		message: t('create_tasks_from_linked_quote'),
		buttons: [
			{
				label: t('project_task_list_create_tasks'),
				isPrimary: true,
				callback: () => {
					window.location.href = url;
				},
			},
		],
	});
};

const handleOpenPhasesModal = () => {
	showPhasesModal.value = true;
};

const handlePhasesModalClose = () => {
	showPhasesModal.value = false;
};

const handlePhasesModalSaved = () => {
	showPhasesModal.value = false;
	refetch();
};

onResult((result) => {
	if (!result.data?.TasksList) {
		return;
	}

	isInitialLoad.value = false;
	delayedDoneFilter.value = selectedDoneFilter.value;

	if (result.data.TasksList.length < LOAD_BATCH_SIZE) {
		isEndReached.value = true;
	}

	if (releaseUnsortedTasksOnNextResult.value) {
		releaseUnsortedTasksOnNextResult.value = false;
		unsortedTasks.value = [];
	}

	if (
		result.data?.TasksList.length === 0 &&
		project.value?.createTasksFromQuoteId &&
		(canCreateTasks.value || canModifyProject.value)
	) {
		showQuoteToTasksMessage();
	}
});

useSocket({
	[SocketMessageType.EVENT_DELETED]: (message) => {
		if (project.value?.id !== String(message.data.project_id)) {
			return;
		}

		const deletedTaskId = String(message.data.event_id);

		unsortedTasks.value = unsortedTasks.value.filter(
			({ id }) => id !== deletedTaskId
		);

		if (message.data.parent_id) {
			const parentId = String(message.data.parent_id);
			if (unsortedSubtasks.value[parentId]) {
				unsortedSubtasks.value[parentId] = unsortedSubtasks.value[
					parentId
				].filter(({ id }) => id !== deletedTaskId);
			}

			const parentCacheId = client.cache.identify({
				__typename: TYPES.Task,
				id: parentId,
			});

			if (parentCacheId) {
				client.cache.modify({
					id: parentCacheId,
					fields: {
						subtasks(
							existingSubtasks: readonly { __ref: string }[],
							{ readField }
						) {
							return existingSubtasks.filter(
								(ref: { __ref: string }) =>
									readField('id', ref) !== deletedTaskId
							);
						},
					},
				});
			}
		}

		delete unsortedSubtasks.value[deletedTaskId];

		client.cache.modify({
			fields: {
				TasksList(
					existingTasks: readonly { __ref: string }[],
					{ readField }
				) {
					return existingTasks.filter(
						(ref: { __ref: string }) =>
							readField('id', ref) !== deletedTaskId
					);
				},
			},
		});

		client.cache.evict({
			id: client.cache.identify({
				__typename: TYPES.Task,
				id: deletedTaskId,
			}),
		});

		client.cache.evict({
			id: client.cache.identify({
				__typename: TYPES.LimitedTask,
				id: deletedTaskId,
			}),
		});

		client.cache.gc();
	},
});

const showInitialSkeletonLoading = computed(() => {
	return loading.value && isInitialLoad.value;
});

const isLoadingMore = computed(() => {
	return (
		loading.value &&
		!isInitialLoad.value &&
		taskListParams.pager.startFrom !== 0
	);
});
</script>

<style lang="scss">
.project-task-list {
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1rem;
	}

	&__title {
		margin-left: 1rem;
	}
}
</style>
